// src/components/gen-application/GeneralApplicationBlankPage.js
import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  memo,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import {
  useGetStep1Query,
  useSaveStep1Mutation,
  useGetStep2Query,
  useSaveStep2Mutation,
  useGetStep3Query,
  useSaveStep3Mutation,
  useGetStep4Query,
  useSaveStep4Mutation,
  useGetStep5Query,
  useSaveStep5Mutation,
  useGetStep6Query,
  useSaveStep6Mutation,
  useGetStep7Query,
  useSaveStep7Mutation,
  useGetStep8Query,
  useSaveStep8Mutation,
  useSaveStep9Mutation,
} from "../../services/gen-app";

import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";
import { SettingsContext } from "../../context/SettingsContext";
import { getSteps } from "./application-steps/index";
import { genStepValidators } from "./genValidators";
import isEqual from "lodash/isEqual";

import PDFPreviewModal from "./PDFPreviewModal";
import ConfirmSubmitDialog from "../helpers/ConfirmSubmitDialog";
import ErrorSummary from "../helpers/ErrorSummary";
import WarningSummary from "../helpers/WarningSummary";

function GeneralApplicationBlankPage({
  scholarshipYear,
  isSaveChanges,
  onSaveComplete,
  isDirty,
  setIsDirty,
  onStatusChange,
  overrideUser,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  const { user, genAppLogoutAuthContext, updateUser, isGenAppOverrideActive } =
    useContext(AuthContext);
  const { isGenAppOpen } = useContext(SettingsContext);
  const effectiveUser = overrideUser || user;
  const appID = effectiveUser?.id;
  const step8HydrationLockRef = useRef(false);
  const didRestoreLocalRef = useRef(false);

  const { data: step1DataResult, isSuccess: step1Loaded } = useGetStep1Query(
    appID,
    {
      skip: !appID,
    }
  );
  const { data: step2DataResult, isSuccess: step2Loaded } = useGetStep2Query(
    appID,
    {
      skip: !appID,
    }
  );
  const { data: step3DataResult, isSuccess: step3Loaded } = useGetStep3Query(
    appID,
    {
      skip: !appID,
    }
  );
  const {
    data: step4DataResult,
    isSuccess: step4Loaded,
    refetch: refetchStep4,
  } = useGetStep4Query(appID, {
    skip: !appID,
  });
  const { data: step5DataResult, isSuccess: step5Loaded } = useGetStep5Query(
    appID,
    {
      skip: !appID,
    }
  );
  const { data: step6DataResult, isSuccess: step6Loaded } = useGetStep6Query(
    appID,
    {
      skip: !appID,
    }
  );
  const {
    data: step7DataResult,
    isSuccess: step7Loaded,
    refetch: refetchStep7,
  } = useGetStep7Query(appID, {
    skip: !appID,
  });
  const { data: step8DataResult, isSuccess: step8Loaded } = useGetStep8Query(
    appID,
    {
      skip: !appID,
    }
  );

  const [saveStep1, { isLoading: isSavingStep1 }] = useSaveStep1Mutation();
  const [saveStep2, { isLoading: isSavingStep2 }] = useSaveStep2Mutation();
  const [saveStep3, { isLoading: isSavingStep3 }] = useSaveStep3Mutation();
  const [saveStep4, { isLoading: isSavingStep4 }] = useSaveStep4Mutation();
  const [saveStep5, { isLoading: isSavingStep5 }] = useSaveStep5Mutation();
  const [saveStep6, { isLoading: isSavingStep6 }] = useSaveStep6Mutation();
  const [saveStep7, { isLoading: isSavingStep7 }] = useSaveStep7Mutation();
  const [saveStep8, { isLoading: isSavingStep8 }] = useSaveStep8Mutation();
  const [saveStep9, { isLoading: isSavingStep9 }] = useSaveStep9Mutation();

  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const initialStepParam = parseInt(queryParams.get("genStep"), 10);
  const initialStep = Number.isNaN(initialStepParam) ? 0 : initialStepParam;
  const [activeStep, setActiveStep] = useState(initialStep);

  const [errors, setErrors] = useState(Array(9).fill({}));
  const [warnings, setWarnings] = useState(Array(9).fill(null));

  const canUserEdit =
    isGenAppOverrideActive ||
    (isGenAppOpen && user?.application_status !== "Submitted");

  const isReadOnly = !canUserEdit;

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    genderOther: "",
    indigenous: "",
    citizenship: "Canadian Citizen",
    SIN: "",
    school_id: "",
    CTC: false,
    address: "",
    city: "",
    province: "",
    postalCode: "",
    country: "Canada",
    cellPhone: "",
    homePhone: "",
    workPhone: "",
    dob: "",
    PEN: "",
    // Step 2 fields:
    careerJobDirection: "",
    directToPostSecondary: "Yes",
    postSecondaryWhen: "",
    PS_Path: "",
    PS_Path_Other: "",
    // Step 2 post-secondary school tracking:
    PS_VIU: false,
    PS_UBC: false,
    PS_UVIC: false,
    PS_SFU: false,
    beenAccepted: false,
    // PS_OTHER is string in DB:
    PS_OTHER_SELECTED: false, // UI-only toggle (NOT saved to DB)
    PS_Other: "", // DB field (string)
    fieldOfStudy: "",
    fieldOfStudyOther: "",
    PS_Program_Name: "",
    otherEducationPlans: "",
    academicSignedDate: "",
    academicSignedName: "",
    // Step 3 fields:
    selectedAwards: [],
    // Step 4 fields:
    school_activity_sports: false,
    school_activity_school_volunteer: false,
    school_activity_school_clubs: false,
    school_activity_students_council: false,
    school_grade_10_activities: "",
    school_grade_10_contact_info: "",
    school_grade_10_hours_served: "",
    school_grade_11_activities: "",
    school_grade_11_contact_info: "",
    school_grade_11_hours_served: "",
    school_grade_12_activities: "",
    school_grade_12_contact_info: "",
    school_grade_12_hours_served: "",
    // Step 5 fields:
    community_grade_10_activities: "",
    community_grade_10_contact_info: "",
    community_grade_10_hours_served: "",
    community_grade_11_activities: "",
    community_grade_11_contact_info: "",
    community_grade_11_hours_served: "",
    community_grade_12_activities: "",
    community_grade_12_contact_info: "",
    community_grade_12_hours_served: "",
    // Step 6 fields:
    personal_info: "",
    choice1: "",
    choice2: "",
    choice3: "",
    choice4: "",
    choice5: "",
    // Step 7 fields:
    affiliations: [],
    // Step 8 fields:
    bursary: false,
    bursaryOptOut: false,
    bursary_resides_with: "",
    bursary_resides_with_other: "",
    bursary_gross_income_father: 0,
    bursary_gross_income_mother: 0,
    bursary_gross_income_stepfather: 0,
    bursary_gross_income_stepmother: 0,
    bursary_gross_income_applicant: 0,
    bursary_gross_income_other: 0,
    bursary_gross_income_other_explain: "",
    bursary_reason: "",
    bursary_siblings: 0,
    bursary_signing_date: "",
    bursary_student_signing: "",
    // Step 9 fields:
    applicationStatus: "",
    showAwardConsiderations: false,
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const steps = useMemo(() => getSteps(), []);

  // after useSaveStep8Mutation:
  const saveBursaryOptOutImmediate = useCallback(
    async (checked) => {
      step8HydrationLockRef.current = true;

      // optimistic local state
      setFormData((prev) => ({ ...prev, bursaryOptOut: checked }));

      try {
        await saveStep8({
          appID,
          data: { bursaryOptOut: checked ? 1 : 0 },
        }).unwrap();

        addNotification(
          checked
            ? "You have opted out of bursary consideration."
            : "Bursary opt-out removed.",
          "success"
        );
      } catch (err) {
        // revert on failure
        setFormData((prev) => ({ ...prev, bursaryOptOut: !checked }));
        addNotification("Failed to save opt-out. Please try again.", "error");
        throw err;
      } finally {
        setTimeout(() => {
          step8HydrationLockRef.current = false;
        }, 250);
      }
    },
    [appID, saveStep8, addNotification, setFormData]
  );

  const checkStepWarnings = useCallback((stepIndex, formData) => {
    const stepWarnings = {};
    switch (stepIndex) {
      case 0:
        // no warnings for step 0
        break;
      case 1:
        if (formData.PS_Path === "Other" && !formData.PS_Path_Other?.trim()) {
          stepWarnings.PS_Path_Other =
            "Please specify your Post Secondary Path details.";
        }
        if (
          formData.fieldOfStudy === "--Other--" &&
          !formData.fieldOfStudyOther?.trim()
        ) {
          stepWarnings.fieldOfStudyOther =
            "Please specify your field of study details.";
        }
        break;
      case 2:
        formData?.selectedAwards?.forEach((award) => {
          const hasGrade = Boolean(
            (award.grade11 && award.grade11.toString().trim()) ||
              (award.grade12 && award.grade12.toString().trim())
          );
          if (!hasGrade && award.moreInfo.trim() === "") {
            stepWarnings[`award_${award?.subjectAwardId}`] =
              "Please provide additional information for the selected award.";
          }
        });
        break;
      case 3:
        if (
          !formData.school_grade_10_activities &&
          !formData.school_grade_11_activities &&
          !formData.school_grade_12_activities
        ) {
          stepWarnings.activities =
            "Please document at least one school activity.";
        }
        break;
      case 4:
        if (
          !formData.community_grade_10_activities &&
          !formData.community_grade_11_activities &&
          !formData.community_grade_12_activities
        ) {
          stepWarnings.activities =
            "Please document at least one community activity.";
        }
        break;
      case 5:
        if (!formData.personal_info.trim()) {
          stepWarnings.personalInfo = "Personal information is incomplete.";
        }
        break;
      case 6:
        formData?.affiliations?.forEach((affiliation, index) => {
          if (
            affiliation.selected &&
            affiliation.moreInfoText &&
            !affiliation.moreInfo.trim()
          ) {
            stepWarnings[
              `affiliation_${affiliation.id}`
            ] = `Please provide more info for ${affiliation.name}.`;
          }
        });
        break;
      case 7:
        if (
          !formData.bursaryOptOut &&
          formData.bursary_gross_income_other > 0 &&
          !formData.bursary_gross_income_other_explain?.trim()
        ) {
          stepWarnings.bursaryOtherExplain =
            "Please explain other financial support.";
        }
        break;
      default:
        break;
    }
    return stepWarnings;
  }, []);

  const validateStep = useCallback(
    (stepIndex, data) => {
      let stepLoaded = false;

      switch (stepIndex) {
        case 0:
          stepLoaded = step1Loaded;
          break;
        case 1:
          stepLoaded = step2Loaded;
          break;
        case 2:
          stepLoaded = step3Loaded;
          break;
        case 3:
          stepLoaded = step4Loaded;
          break;
        case 4:
          stepLoaded = step5Loaded;
          break;
        case 5:
          stepLoaded = step6Loaded;
          break;
        case 6:
          stepLoaded = step7Loaded;
          break;
        case 7:
          stepLoaded = step8Loaded;
          break;
        case 8:
        default:
          stepLoaded = true;
          break;
      }

      if (!stepLoaded) return true;

      const validatorFn = genStepValidators[stepIndex];
      if (!validatorFn) return true;

      const stepErrs = validatorFn(data);

      setErrors((prevErrors) => {
        const next = [...prevErrors];
        next[stepIndex] = stepErrs;
        return isEqual(next[stepIndex], prevErrors[stepIndex])
          ? prevErrors
          : next;
      });

      const stepWarnings = checkStepWarnings(stepIndex, data);

      setWarnings((prevWarnings) => {
        const next = [...prevWarnings];
        next[stepIndex] =
          stepWarnings && Object.keys(stepWarnings).length > 0
            ? stepWarnings
            : null;

        return isEqual(next[stepIndex], prevWarnings[stepIndex])
          ? prevWarnings
          : next;
      });

      return Object.keys(stepErrs).length === 0;
    },
    [
      step1Loaded,
      step2Loaded,
      step3Loaded,
      step4Loaded,
      step5Loaded,
      step6Loaded,
      step7Loaded,
      step8Loaded,
      checkStepWarnings,
    ]
  );

  useEffect(() => {
    if (!isDataLoaded) return;

    const t = setTimeout(() => {
      validateStep(activeStep, formData);
    }, 150);

    return () => clearTimeout(t);
  }, [formData, activeStep, isDataLoaded, validateStep]);

  const toBool = (v) => v === true || v === 1 || v === "1" || v === "true";

  const handleChange = (field) => (e) => {
    const target = e.target;
    let value;

    if (target.type === "checkbox") {
      value = target.checked;
    } else if (target.type === "radio") {
      value = target.value;
    } else if (target.type === "number") {
      value = target.value === "" ? "" : Number(target.value);
    } else {
      value = target.value;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getErrorSteps = () => {
    const errorStepIndices = [];
    errors?.forEach((stepErr, index) => {
      if (stepErr && Object.keys(stepErr).length > 0) {
        errorStepIndices.push(index);
      }
    });
    warnings?.forEach((warningObj, index) => {
      if (warningObj && Object.keys(warningObj).length > 0) {
        if (!errorStepIndices.includes(index)) {
          errorStepIndices.push(index);
        }
      }
    });

    return errorStepIndices;
  };

  useEffect(() => {
    if (step1Loaded && step1DataResult) {
      const step1Data = step1DataResult.data;
      const predefinedGenders = [
        "Male",
        "Female",
        "Non-binary",
        "prefer-not-to-say",
      ];
      const isPredefinedGender = predefinedGenders.includes(step1Data.gender);

      setFormData((prev) => ({
        ...prev,
        // Step 1 fields:
        firstName: step1Data.first_name || "",
        middleName: step1Data.middle_name || "",
        lastName: step1Data.last_name || "",
        gender: isPredefinedGender ? step1Data.gender : "self-describe",
        indigenous: step1Data.indigenous || "",
        genderOther: isPredefinedGender ? "" : step1Data.gender,
        citizenship: step1Data.citizenship || "Canadian Citizen",
        SIN: step1Data.SIN || "",
        school_id: step1Data.school_id || "",
        showAwardConsiderations:
          step1Data.showAwardConsiderations === true ||
          step1Data.showAwardConsiderations === 1 ||
          step1Data.showAwardConsiderations === "1" ||
          step1Data.showAwardConsiderations === "true",
        CTC:
          step1Data.CTC === 1 ||
          step1Data.CTC === "true" ||
          step1Data.CTC === true,
        address: step1Data.address || "",
        city: step1Data.city || "",
        province: step1Data.province || "",
        postalCode: step1Data.postalCode || "",
        country: step1Data.country || "Canada",
        cellPhone: step1Data.cellPhone || "",
        homePhone: step1Data.homePhone || "",
        workPhone: step1Data.workPhone || "",
        dob: step1Data.dob ? step1Data.dob.split("T")[0] : "",
        PEN: step1Data.PEN || "",
      }));
    }
  }, [step1Loaded, step1DataResult]);

  useEffect(() => {
    if (formData.showAwardConsiderations) return;

    // If this school doesn't use award considerations, don't keep stale picks.
    if (
      formData.choice1 ||
      formData.choice2 ||
      formData.choice3 ||
      formData.choice4 ||
      formData.choice5
    ) {
      setFormData((prev) => ({
        ...prev,
        choice1: "",
        choice2: "",
        choice3: "",
        choice4: "",
        choice5: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.showAwardConsiderations]);

  useEffect(() => {
    if (step2Loaded && step2DataResult) {
      const step2Data = step2DataResult.data;

      const psOther = step2Data.PS_Other || "";

      setFormData((prev) => ({
        ...prev,
        // Step 2 fields:
        careerJobDirection: step2Data.career_job_direction || "",
        directToPostSecondary: step2Data.direct_to_post_secondary
          ? "Yes"
          : "No",
        postSecondaryWhen: step2Data.post_secondary_when || "",
        PS_Path: step2Data.PS_Path || "",
        PS_Path_Other: step2Data.PS_Path_Other || "",
        // ✅ New fields:
        // ✅ New fields:
        PS_VIU: toBool(step2Data.PS_VIU),
        PS_UBC: toBool(step2Data.PS_UBC),
        PS_UVIC: toBool(step2Data.PS_UVIC),
        PS_SFU: toBool(step2Data.PS_SFU),
        PS_OTHER_SELECTED: Boolean(psOther.trim()),
        PS_Other: psOther,
        beenAccepted: toBool(step2Data.beenAccepted),
        fieldOfStudy: step2Data.Field_of_Study || "",
        fieldOfStudyOther: step2Data.Field_of_Study_Other || "",
        PS_Program_Name: step2Data.PS_Program_Name || "",
        otherEducationPlans: step2Data.other_education_plans || "",
        academicSignedDate: step2Data.academic_signed_date
          ? step2Data.academic_signed_date.split("T")[0]
          : "",
        academicSignedName: step2Data.academic_signed_name || "",
      }));
    }
  }, [step2Loaded, step2DataResult]);

  useEffect(() => {
    if (step3Loaded && step3DataResult) {
      const step3Data = step3DataResult.data;

      // parse moreinfo into grade11/grade12/additional info for the UI
      const helpers = require("./awardMoreInfoHelpers");
      const initializedAwards = step3Data.map((award) => {
        const parsed = helpers.parseMoreInfo(award.moreinfo || "");
        return {
          subjectAwardId: award.subject_award_id,
          grade11: parsed.grade11,
          grade12: parsed.grade12,
          moreInfo: parsed.moreInfo,
        };
      });

      setFormData((prev) => ({
        ...prev,
        selectedAwards: initializedAwards,
      }));
    }
  }, [step3Loaded, step3DataResult]);

  useEffect(() => {
    if (step4Loaded && step4DataResult) {
      const step4Data = step4DataResult.data;

      setFormData((prev) => ({
        ...prev,
        school_activity_sports: step4Data.school_activity_sports || false,
        school_activity_school_volunteer:
          step4Data.school_activity_school_volunteer || false,
        school_activity_school_clubs:
          step4Data.school_activity_school_clubs || false,
        school_activity_students_council:
          step4Data.school_activity_students_council || false,
        school_grade_10_activities: step4Data.school_grade_10_activities || "",
        school_grade_10_contact_info:
          step4Data.school_grade_10_contact_info || "",
        school_grade_10_hours_served:
          step4Data.school_grade_10_hours_served || "",
        school_grade_11_activities: step4Data.school_grade_11_activities || "",
        school_grade_11_contact_info:
          step4Data.school_grade_11_contact_info || "",
        school_grade_11_hours_served:
          step4Data.school_grade_11_hours_served || "",
        school_grade_12_activities: step4Data.school_grade_12_activities || "",
        school_grade_12_contact_info:
          step4Data.school_grade_12_contact_info || "",
        school_grade_12_hours_served:
          step4Data.school_grade_12_hours_served || "",
      }));
    }
  }, [step4Loaded, step4DataResult]);

  useEffect(() => {
    if (step5Loaded && step5DataResult) {
      const step5Data = step5DataResult.data;

      setFormData((prev) => ({
        ...prev,
        community_grade_10_activities:
          step5Data.community_grade_10_activities || "",
        community_grade_10_contact_info:
          step5Data.community_grade_10_contact_info || "",
        community_grade_10_hours_served:
          step5Data.community_grade_10_hours_served || "",
        community_grade_11_activities:
          step5Data.community_grade_11_activities || "",
        community_grade_11_contact_info:
          step5Data.community_grade_11_contact_info || "",
        community_grade_11_hours_served:
          step5Data.community_grade_11_hours_served || "",
        community_grade_12_activities:
          step5Data.community_grade_12_activities || "",
        community_grade_12_contact_info:
          step5Data.community_grade_12_contact_info || "",
        community_grade_12_hours_served:
          step5Data.community_grade_12_hours_served || "",
      }));
    }
  }, [step5Loaded, step5DataResult]);

  useEffect(() => {
    if (step6Loaded && step6DataResult) {
      const step6Data = step6DataResult.data;

      setFormData((prev) => ({
        ...prev,
        personal_info: step6Data?.personal_info || "",
        choice1: step6Data.choice1 || "",
        choice2: step6Data.choice2 || "",
        choice3: step6Data.choice3 || "",
        choice4: step6Data.choice4 || "",
        choice5: step6Data.choice5 || "",
      }));
    }
  }, [step6Loaded, step6DataResult]);

  useEffect(() => {
    if (step7Loaded && step7DataResult) {
      const step7Data = step7DataResult.data;

      const formatedAffiliations = step7Data.affiliations.map((aff) => ({
        id: aff.id,
        name: aff.Affiliation,
        moreInfoText: aff.MoreInfoText,
        selected: aff.selected || false,
        moreInfo: aff.moreInfo || "",
      }));

      setFormData((prev) => ({
        ...prev,
        affiliations: formatedAffiliations,
      }));
    }
  }, [step7Loaded, step7DataResult]);

  useEffect(() => {
    if (!step8Loaded || !step8DataResult) return;
    if (step8HydrationLockRef.current) return;

    const step8Data = step8DataResult.data;

    setFormData((prev) => ({
      ...prev,
      bursaryOptOut: toBool(step8Data.bursaryOptOut),
      bursary_resides_with: step8Data.bursary_resides_with || "",
      bursary_resides_with_other: step8Data.bursary_resides_with_other || "",
      bursary_gross_income_father: step8Data.bursary_gross_income_father || 0,
      bursary_gross_income_mother: step8Data.bursary_gross_income_mother || 0,
      bursary_gross_income_stepfather:
        step8Data.bursary_gross_income_stepfather || 0,
      bursary_gross_income_stepmother:
        step8Data.bursary_gross_income_stepmother || 0,
      bursary_gross_income_applicant:
        step8Data.bursary_gross_income_applicant || 0,
      bursary_gross_income_other: step8Data.bursary_gross_income_other || 0,
      bursary_gross_income_other_explain:
        step8Data.bursary_gross_income_other_explain || "",
      bursary_reason: step8Data.bursary_reason || "",
      bursary_siblings: step8Data.bursary_siblings || 0,
      bursary_signing_date: step8Data.bursary_signing_date
        ? new Date(step8Data.bursary_signing_date).toISOString().split("T")[0]
        : "",
      bursary_student_signing: step8Data.bursary_student_signing || "",
    }));
  }, [step8Loaded, step8DataResult]);

  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (String(activeStep) !== params.get("genStep")) {
      params.set("genStep", String(activeStep));
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [activeStep, location.search, location.pathname, navigate]);

  useEffect(() => {
    if (didRestoreLocalRef.current) return;
    const storedFormData = localStorage.getItem("genFormData");
    if (storedFormData) {
      const parsed = JSON.parse(storedFormData);
      const isEmptyValue = (v) =>
        v === "" ||
        v === false ||
        v === null ||
        v === undefined ||
        (Array.isArray(v) && v.length === 0) ||
        (typeof v === "number" && v === 0);

      const stillEmpty = Object.values(formData).every(isEmptyValue);

      if (stillEmpty) {
        setFormData(parsed);
        didRestoreLocalRef.current = true;
      }
    }
    const allStepsLoaded =
      step1Loaded &&
      step2Loaded &&
      step3Loaded &&
      step4Loaded &&
      step5Loaded &&
      step6Loaded &&
      step7Loaded &&
      step8Loaded;

    if (allStepsLoaded) {
      setIsDataLoaded(true);
    } // eslint-disable-next-line
  }, [
    step1Loaded,
    step2Loaded,
    step3Loaded,
    step4Loaded,
    step5Loaded,
    step6Loaded,
    step7Loaded,
    step8Loaded,
  ]);

  useEffect(() => {
    if (isDataLoaded && initialFormData === null) {
      setInitialFormData(formData);
    }
  }, [isDataLoaded, initialFormData, formData]);

  useEffect(() => {
    if (!isDataLoaded || !initialFormData) return;

    if (!isEqual(formData, initialFormData)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [formData, initialFormData, isDataLoaded, setIsDirty]);

  useEffect(() => {
    localStorage.setItem("genFormData", JSON.stringify(formData));
  }, [formData]);

  const handleTabChange = async (event, newValue) => {
    if (!isDataLoaded) {
      setActiveStep(newValue);
      return;
    }

    if (initialFormData && !isEqual(formData, initialFormData)) {
      await handleSaveAndContinue(false, {
        advance: false,
        snapshot: formData,
      });
      // DO NOT setInitialFormData(formData) here (see #2)
    }

    setActiveStep(newValue);
    validateStep(newValue, formData);
  };

  const handleSaveAndContinue = async (
    finalSubmit = false,
    { advance = true, snapshot } = {}
  ) => {
    if (!isDataLoaded) {
      addNotification("The data is not Loaded.", "error");
      return;
    }

    if (!canUserEdit) {
      addNotification(
        "You do not have permission to save changes as the application is closed.",
        "error"
      );
      return;
    }

    if (!appID) {
      addNotification("User not authenticated.", "error");
      return;
    }

    const dataSnapshot = snapshot ?? formData;

    const isStepValid = validateStep(activeStep, dataSnapshot);
    if (!isStepValid) {
      addNotification(
        "General App: Some required fields are still missing or invalid. " +
          "We saved your progress, but you must fix these before final submission.",
        "warning"
      );
    }

    let dataToSave = {};
    let saveFunction = null;

    if (activeStep === 0) {
      const genderToSave =
        dataSnapshot.gender === "self-describe"
          ? dataSnapshot.genderOther
          : dataSnapshot.gender;

      dataToSave = {
        firstName: dataSnapshot.firstName,
        middleName: dataSnapshot.middleName,
        lastName: dataSnapshot.lastName,
        gender: genderToSave,
        indigenous: dataSnapshot.indigenous,
        citizenship: dataSnapshot.citizenship,
        SIN: dataSnapshot.SIN,
        schoolId: dataSnapshot.school_id,
        CTC: dataSnapshot.CTC,
        address: dataSnapshot.address,
        city: dataSnapshot.city,
        province: dataSnapshot.province,
        postalCode: dataSnapshot.postalCode,
        country: dataSnapshot.country,
        cellPhone: dataSnapshot.cellPhone,
        homePhone: dataSnapshot.homePhone,
        workPhone: dataSnapshot.workPhone,
        dob: dataSnapshot.dob,
        PEN: dataSnapshot.PEN,
      };
      saveFunction = saveStep1;
    } else if (activeStep === 1) {
      dataToSave = {
        careerJobDirection: dataSnapshot.careerJobDirection,
        directToPostSecondary: dataSnapshot.directToPostSecondary === "Yes",
        postSecondaryWhen: dataSnapshot.postSecondaryWhen,
        PS_Path: dataSnapshot.PS_Path,
        PS_Path_Other: dataSnapshot.PS_Path_Other,
        PS_VIU: dataSnapshot.PS_VIU,
        PS_UBC: dataSnapshot.PS_UBC,
        PS_UVIC: dataSnapshot.PS_UVIC,
        PS_SFU: dataSnapshot.PS_SFU,
        beenAccepted: dataSnapshot.beenAccepted,
        PS_Other: dataSnapshot.PS_OTHER_SELECTED
          ? dataSnapshot.PS_Other || ""
          : "",
        fieldOfStudy: dataSnapshot.fieldOfStudy,
        fieldOfStudyOther: dataSnapshot.fieldOfStudyOther,
        PS_Program_Name: dataSnapshot.PS_Program_Name,
        otherEducationPlans: dataSnapshot.otherEducationPlans,
        academicSignedDate: dataSnapshot.academicSignedDate,
        academicSignedName: dataSnapshot.academicSignedName,
      };
      saveFunction = saveStep2;
    } else if (activeStep === 2) {
      // Step 3: Award Selection - save only selected awards and combine grades/additional info into `moreinfo` for backend
      const helpers = require("./awardMoreInfoHelpers");
      const selectedAwardsToSave = (dataSnapshot.selectedAwards || []).map(
        (award) => ({
          subject_award_id: award.subjectAwardId,
          moreinfo: helpers.buildMoreInfo({
            grade11: award.grade11,
            grade12: award.grade12,
            moreInfo: award.moreInfo,
          }),
        })
      );

      dataToSave = { selectedAwards: selectedAwardsToSave };
      saveFunction = saveStep3;
    } else if (activeStep === 3) {
      dataToSave = {
        school_activity_sports: dataSnapshot.school_activity_sports,
        school_activity_school_volunteer:
          dataSnapshot.school_activity_school_volunteer,
        school_activity_school_clubs: dataSnapshot.school_activity_school_clubs,
        school_activity_students_council:
          dataSnapshot.school_activity_students_council,
        school_grade_10_activities: dataSnapshot.school_grade_10_activities,
        school_grade_10_contact_info: dataSnapshot.school_grade_10_contact_info,
        school_grade_10_hours_served: dataSnapshot.school_grade_10_hours_served,
        school_grade_11_activities: dataSnapshot.school_grade_11_activities,
        school_grade_11_contact_info: dataSnapshot.school_grade_11_contact_info,
        school_grade_11_hours_served: dataSnapshot.school_grade_11_hours_served,
        school_grade_12_activities: dataSnapshot.school_grade_12_activities,
        school_grade_12_contact_info: dataSnapshot.school_grade_12_contact_info,
        school_grade_12_hours_served: dataSnapshot.school_grade_12_hours_served,
      };
      saveFunction = saveStep4;
    } else if (activeStep === 4) {
      dataToSave = {
        community_grade_10_activities:
          dataSnapshot.community_grade_10_activities,
        community_grade_10_contact_info:
          dataSnapshot.community_grade_10_contact_info,
        community_grade_10_hours_served:
          dataSnapshot.community_grade_10_hours_served,
        community_grade_11_activities:
          dataSnapshot.community_grade_11_activities,
        community_grade_11_contact_info:
          dataSnapshot.community_grade_11_contact_info,
        community_grade_11_hours_served:
          dataSnapshot.community_grade_11_hours_served,
        community_grade_12_activities:
          dataSnapshot.community_grade_12_activities,
        community_grade_12_contact_info:
          dataSnapshot.community_grade_12_contact_info,
        community_grade_12_hours_served:
          dataSnapshot.community_grade_12_hours_served,
      };
      saveFunction = saveStep5;
    } else if (activeStep === 5) {
      dataToSave = {
        personal_info: dataSnapshot.personal_info,
        choice1: dataSnapshot.choice1,
        choice2: dataSnapshot.choice2,
        choice3: dataSnapshot.choice3,
        choice4: dataSnapshot.choice4,
        choice5: dataSnapshot.choice5,
      };
      saveFunction = saveStep6;
    } else if (activeStep === 6) {
      const affiliationsToSave = (dataSnapshot.affiliations || [])
        .filter((aff) => aff.selected)
        .map((aff) => ({
          affiliation_id: aff.id,
          moreinfo: aff.moreInfo,
        }));

      dataToSave = { affiliations: affiliationsToSave };
      saveFunction = saveStep7;
    } else if (activeStep === 7) {
      if (dataSnapshot.bursaryOptOut) {
        dataToSave = { bursaryOptOut: 1 };
      } else {
        const num = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : 0;
        };

        dataToSave = {
          bursaryOptOut: 0,
          bursary_resides_with: dataSnapshot.bursary_resides_with || "",
          bursary_resides_with_other:
            dataSnapshot.bursary_resides_with_other || "",
          bursary_gross_income_father: num(
            dataSnapshot.bursary_gross_income_father
          ),
          bursary_gross_income_mother: num(
            dataSnapshot.bursary_gross_income_mother
          ),
          bursary_gross_income_stepfather: num(
            dataSnapshot.bursary_gross_income_stepfather
          ),
          bursary_gross_income_stepmother: num(
            dataSnapshot.bursary_gross_income_stepmother
          ),
          bursary_gross_income_applicant: num(
            dataSnapshot.bursary_gross_income_applicant
          ),
          bursary_gross_income_other: num(
            dataSnapshot.bursary_gross_income_other
          ),
          bursary_gross_income_other_explain:
            dataSnapshot.bursary_gross_income_other_explain || "",
          bursary_reason: dataSnapshot.bursary_reason || "",
          bursary_siblings: Number.isFinite(
            Number(dataSnapshot.bursary_siblings)
          )
            ? parseInt(dataSnapshot.bursary_siblings, 10)
            : 0,
          bursary_signing_date: dataSnapshot.bursary_signing_date || "",
          bursary_student_signing: dataSnapshot.bursary_student_signing || "",
        };
      }

      saveFunction = saveStep8;
    } else if (activeStep === 8) {
      // Step 9: Review & Submit
      if (user?.application_status === "Submitted") {
        addNotification("Application has already been submitted.", "info");
        return;
      }

      const isValid = validateStep(8, formData);
      if (!isValid) {
        addNotification(
          "General App: Please fix errors before submitting.",
          "error"
        );
        return;
      }

      let allValid = true;
      for (let i = 0; i < 9; i++) {
        const stepOk = validateStep(i, formData);
        if (!stepOk) allValid = false;
      }

      if (!allValid && finalSubmit) {
        const errorSteps = getErrorSteps();
        const errorStepLabels = errorSteps.map(
          (stepIndex) => steps[stepIndex].label
        );
        addNotification(
          `General App: Cannot submit application. Please fix errors in the following steps: ${errorStepLabels.join(
            ", "
          )}.`,
          "error"
        );
        return;
      }

      // ✅ Save & Exit from Step 9 should NOT call saveStep9
      if (!finalSubmit) {
        addNotification("Progress saved successfully.", "success");
        onSaveComplete();
        setTimeout(() => genAppLogoutAuthContext(), 1000);
        return; // <-- critical
      }

      // ✅ Only submit when finalSubmit = true
      dataToSave = { submitted: true };
      saveFunction = saveStep9;
    }

    if (dataToSave && saveFunction) {
      try {
        const result = await saveFunction({ appID, data: dataToSave }).unwrap();

        if (activeStep === 8 && finalSubmit) {
          if (!overrideUser) {
            updateUser({
              application_status: "Submitted",
              access_override_until: null,
            });
          }

          setFormData((prev) => ({ ...prev, applicationStatus: "Submitted" }));

          addNotification(
            result?.message || "Application submitted successfully.",
            "success"
          );
        } else {
          addNotification(
            result?.message || "Progress saved successfully.",
            "success"
          );

          if (advance && activeStep < steps.length - 1) {
            setActiveStep((prev) => prev + 1);
          }
        }

        setInitialFormData(dataSnapshot);

        if (activeStep === 0) {
          refetchStep4?.();
          refetchStep7?.();
        }
      } catch (err) {
        console.error("Save and Continue Error:", err);
        addNotification("Failed to save progress. Please try again.", "error");
      }
    }
  };

  const handleSaveAndExit = async (finalSubmit = false) => {
    if (!isDataLoaded) return;

    if (!canUserEdit) {
      addNotification(
        "You do not have permission to save changes as the application is closed.",
        "error"
      );
      return;
    }

    if (!appID) {
      addNotification("User not authenticated.", "error");
      return;
    }

    const isStepValid = validateStep(activeStep, formData);
    if (!isStepValid) {
      addNotification(
        "General App: Some required fields are still missing or invalid. " +
          "We saved your progress, but you must fix these before final submission.",
        "warning"
      );
    }

    let dataToSave = {};
    let saveFunction = null;

    if (activeStep === 0) {
      // Step 1: Personal Information
      const genderToSave =
        formData.gender === "self-describe"
          ? formData.genderOther
          : formData.gender;

      dataToSave = {
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        gender: genderToSave,
        indigenous: formData.indigenous,
        citizenship: formData.citizenship,
        SIN: formData.SIN,
        schoolId: formData.school_id,
        CTC: formData.CTC,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postalCode: formData.postalCode,
        country: formData.country,
        cellPhone: formData.cellPhone,
        homePhone: formData.homePhone,
        workPhone: formData.workPhone,
        dob: formData.dob,
        PEN: formData.PEN,
      };
      saveFunction = saveStep1;
    } else if (activeStep === 1) {
      // Step 2: Academic Information
      dataToSave = {
        careerJobDirection: formData.careerJobDirection,
        directToPostSecondary:
          formData.directToPostSecondary === "Yes" ? true : false,
        postSecondaryWhen: formData.postSecondaryWhen,
        PS_Path: formData.PS_Path,
        PS_Path_Other: formData.PS_Path_Other,

        PS_VIU: formData.PS_VIU,
        PS_UBC: formData.PS_UBC,
        PS_UVIC: formData.PS_UVIC,
        PS_SFU: formData.PS_SFU,
        PS_Other: formData.PS_OTHER_SELECTED ? formData.PS_Other || "" : "",

        fieldOfStudy: formData.fieldOfStudy,
        fieldOfStudyOther: formData.fieldOfStudyOther,
        PS_Program_Name: formData.PS_Program_Name,
        otherEducationPlans: formData.otherEducationPlans,
        academicSignedDate: formData.academicSignedDate,
        academicSignedName: formData.academicSignedName,
      };
      saveFunction = saveStep2;
    } else if (activeStep === 2) {
      // Step 3: Award Selection
      dataToSave = {
        selectedAwards: formData.selectedAwards,
      };
      saveFunction = saveStep3;
    } else if (activeStep === 3) {
      // Step 4: School Activities Documentation
      dataToSave = {
        school_activity_sports: formData.school_activity_sports,
        school_activity_school_volunteer:
          formData.school_activity_school_volunteer,
        school_activity_school_clubs: formData.school_activity_school_clubs,
        school_activity_students_council:
          formData.school_activity_students_council,
        school_grade_10_activities: formData.school_grade_10_activities,
        school_grade_10_contact_info: formData.school_grade_10_contact_info,
        school_grade_10_hours_served: formData.school_grade_10_hours_served,
        school_grade_11_activities: formData.school_grade_11_activities,
        school_grade_11_contact_info: formData.school_grade_11_contact_info,
        school_grade_11_hours_served: formData.school_grade_11_hours_served,
        school_grade_12_activities: formData.school_grade_12_activities,
        school_grade_12_contact_info: formData.school_grade_12_contact_info,
        school_grade_12_hours_served: formData.school_grade_12_hours_served,
      };
      saveFunction = saveStep4;
    } else if (activeStep === 4) {
      // Step 5: Community Activities Documentation
      dataToSave = {
        community_grade_10_activities: formData.community_grade_10_activities,
        community_grade_10_contact_info:
          formData.community_grade_10_contact_info,
        community_grade_10_hours_served:
          formData.community_grade_10_hours_served,
        community_grade_11_activities: formData.community_grade_11_activities,
        community_grade_11_contact_info:
          formData.community_grade_11_contact_info,
        community_grade_11_hours_served:
          formData.community_grade_11_hours_served,
        community_grade_12_activities: formData.community_grade_12_activities,
        community_grade_12_contact_info:
          formData.community_grade_12_contact_info,
        community_grade_12_hours_served:
          formData.community_grade_12_hours_served,
      };
      saveFunction = saveStep5;
    } else if (activeStep === 5) {
      // Step 6: Personal Information
      dataToSave = {
        personal_info: formData.personal_info,
        choice1: formData.choice1,
        choice2: formData.choice2,
        choice3: formData.choice3,
        choice4: formData.choice4,
        choice5: formData.choice5,
      };
      saveFunction = saveStep6;
    } else if (activeStep === 6) {
      // Step 7: Affiliations
      const affiliationsToSave = formData.affiliations
        .filter((aff) => aff.selected)
        .map((aff) => ({
          affiliation_id: aff.id,
          moreinfo: aff.moreInfo,
        }));

      dataToSave = {
        affiliations: affiliationsToSave,
      };
      saveFunction = saveStep7;
    } else if (activeStep === 7) {
      // Step 8: Bursary Declaration
      dataToSave = {
        bursaryOptOut: formData.bursaryOptOut ? 1 : 0,
        bursary_resides_with: formData.bursary_resides_with,
        bursary_resides_with_other: formData.bursary_resides_with_other || "",
        bursary_gross_income_father: parseFloat(
          formData.bursary_gross_income_father
        ),
        bursary_gross_income_mother: parseFloat(
          formData.bursary_gross_income_mother
        ),
        bursary_gross_income_stepfather: parseFloat(
          formData.bursary_gross_income_stepfather
        ),
        bursary_gross_income_stepmother: parseFloat(
          formData.bursary_gross_income_stepmother
        ),
        bursary_gross_income_applicant: parseFloat(
          formData.bursary_gross_income_applicant
        ),
        bursary_gross_income_other: parseFloat(
          formData.bursary_gross_income_other
        ),
        bursary_gross_income_other_explain:
          formData.bursary_gross_income_other_explain || "",
        bursary_reason: formData.bursary_reason || "",
        bursary_siblings: parseInt(formData.bursary_siblings, 10) || 0,
        bursary_signing_date: formData.bursary_signing_date,
        bursary_student_signing: formData.bursary_student_signing,
      };
      saveFunction = saveStep8;
    } else if (activeStep === 8) {
      // Step 9: Review & Submit
      if (user?.application_status === "Submitted") {
        addNotification("Application has already been submitted.", "info");
        return;
      }

      const isValid = validateStep(8, formData);
      if (!isValid) {
        addNotification(
          "General App: Please fix errors before submitting.",
          "error"
        );
        return;
      }
      let allValid = true;
      for (let i = 0; i < 9; i++) {
        const stepOk = validateStep(i, formData);
        if (!stepOk) allValid = false;
      }
      if (!allValid && finalSubmit) {
        const errorSteps = getErrorSteps();
        const errorStepLabels = errorSteps.map(
          (stepIndex) => steps[stepIndex].label
        );
        addNotification(
          `General App: Cannot submit application. Please fix errors in the following steps: ${errorStepLabels.join(
            ", "
          )}.`,
          "error"
        );
        return;
      }

      if (!finalSubmit) {
        addNotification("Progress saved successfully.", "success");
        onSaveComplete();
        setTimeout(() => {
          genAppLogoutAuthContext();
        }, 1000);
      }

      dataToSave = {};
      saveFunction = saveStep9;
    }

    if (dataToSave && saveFunction) {
      try {
        const result = await saveFunction({ appID, data: dataToSave }).unwrap();
        if (activeStep === 8 && finalSubmit) {
          if (!overrideUser) {
            updateUser({
              application_status: "Submitted",
              access_override_until: null,
            });
          }
          setFormData((prev) => ({
            ...prev,
            applicationStatus: "Submitted",
          }));

          addNotification(
            result?.message || "Application submitted successfully.",
            "success"
          );
        } else {
          addNotification(
            result?.message || "Your progress has been saved.",
            "success"
          );
          genAppLogoutAuthContext();
        }
        setInitialFormData(formData);
      } catch (err) {
        console.error("Save and Exit Error:", err);
        addNotification("Failed to save progress. Please try again.", "error");
      }
    }
  };

  useEffect(() => {
    if (activeStep === steps.length - 1 && isDataLoaded) {
      steps.forEach((_, index) => {
        validateStep(index, formData);
      });
    }
  }, [formData, activeStep, isDataLoaded, steps, validateStep]);

  useEffect(() => {
    if (isSaveChanges) {
      if (!isDirty) {
        onSaveComplete();
        return;
      }

      if (initialFormData && !isEqual(formData, initialFormData)) {
        handleSaveAndContinue(false)
          .then(() => {
            onSaveComplete();
            setIsDirty(false);
          })
          .catch((error) => {
            console.error("Save operation failed:", error);
            addNotification(
              "Failed to save progress. Please try again.",
              "error"
            );
            onSaveComplete();
          });
      } else {
        onSaveComplete();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveChanges]);

  useEffect(() => {
    const anyErrors = errors.some(
      (stepErr) => stepErr && Object.keys(stepErr).length > 0
    );
    const anyWarnings = warnings.some(
      (warningObj) => warningObj && Object.keys(warningObj).length > 0
    );

    if (anyErrors) {
      onStatusChange("error");
    } else if (anyWarnings) {
      onStatusChange("warning");
    } else {
      onStatusChange("success");
    }
  }, [errors, warnings, onStatusChange]);

  const handlePreviewDialogOpen = () => setOpenPreviewDialog(true);
  const handlePreviewDialogClose = () => setOpenPreviewDialog(false);

  const isSavingSteps =
    isSavingStep1 ||
    isSavingStep2 ||
    isSavingStep3 ||
    isSavingStep4 ||
    isSavingStep5 ||
    isSavingStep6 ||
    isSavingStep7 ||
    isSavingStep8 ||
    isSavingStep9;

  const hasErrors = errors.some(
    (stepErr) => stepErr && Object.keys(stepErr).length > 0
  );

  const [summaryTab, setSummaryTab] = useState(0);

  const errorSummaryItems = errors
    .map((stepErr, index) =>
      stepErr && Object.keys(stepErr).length > 0
        ? { stepIndex: index, fields: stepErr }
        : null
    )
    .filter((s) => s !== null);

  const warningSummaryItems = warnings
    .map((warningObj, index) =>
      warningObj && Object.keys(warningObj).length > 0
        ? { stepIndex: index, warnings: warningObj }
        : null
    )
    .filter((s) => s !== null);

  useEffect(() => {
    const count =
      (errorSummaryItems.length ? 1 : 0) + (warningSummaryItems.length ? 1 : 0);
    if (count === 0) return;
    if (summaryTab >= count) setSummaryTab(0);
  }, [errorSummaryItems.length, warningSummaryItems.length, summaryTab]);

  return (
    <div className="application-blank-page">
      <Box sx={{ width: "100%", typography: "body1" }}>
        <Tabs
          value={activeStep}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Application Steps"
        >
          {steps?.map((step, index) => {
            const stepErrObj = errors[index] || {};
            const stepWarning = warnings[index] || null;

            const noErrors = Object.keys(stepErrObj).length === 0;
            const hasWarnings = stepWarning;

            let iconToShow;
            let tooltipTitle = "";

            if (!noErrors) {
              iconToShow = <ErrorOutlineIcon color="error" />;
              tooltipTitle = Object.values(stepErrObj).join(" ");
            } else if (hasWarnings) {
              iconToShow = <WarningAmberIcon sx={{ color: "#ffd500" }} />;
              tooltipTitle = Object.values(stepWarning).join(" ");
            } else {
              iconToShow = <CheckCircleIcon color="success" />;
            }

            return (
              <Tooltip
                key={`gen-step-${index}`}
                title={tooltipTitle}
                placement="top"
                arrow
                disableHoverListener={!tooltipTitle}
              >
                <Tab
                  icon={iconToShow}
                  iconPosition="start"
                  label={step?.label}
                  sx={{
                    backgroundColor: activeStep === index ? "#3e9d5f" : "#eee",
                    color: activeStep === index ? "#ffffff" : "#000000",
                    "&.Mui-selected": {
                      backgroundColor: "#3e9d5f",
                      color: "#ffffff",
                    },
                    "&:hover": {
                      backgroundColor: "#5DC1AF",
                    },
                    borderRadius: "15px 15px 0 0",
                    border: "1px solid #a3a3a3",
                    textTransform: "none",
                    position: "relative",
                    borderBottom: !noErrors
                      ? "3px solid #f44336"
                      : hasWarnings
                      ? "3px solid #ffd500"
                      : "1px solid #a3a3a3",
                  }}
                />
              </Tooltip>
            );
          })}
        </Tabs>
        {user?.application_status === "Submitted" &&
          !isGenAppOverrideActive && (
            <div className="notice-banner">
              Application has been submitted - No Changes permitted
            </div>
          )}
        {!isGenAppOpen &&
          !isGenAppOverrideActive &&
          user?.application_status !== "Submitted" && (
            <div className="notice-banner">
              Application period is now closed - No Changes Permitted
            </div>
          )}
        <Box sx={{ p: 2.5, m: 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
            className="form-group"
          >
            {activeStep === steps.length - 1 && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={800} textAlign="center">
                  Review & Submit
                </Typography>
              </Stack>
            )}
            {activeStep === steps.length - 1 && (
              <>
                {(errorSummaryItems.length > 0 ||
                  warningSummaryItems.length > 0) && (
                  <Tabs
                    value={summaryTab}
                    onChange={(event, newValue) => setSummaryTab(newValue)}
                    aria-label="Summary Tabs"
                    sx={{ mb: 2 }}
                  >
                    {errorSummaryItems.length > 0 && (
                      <Tab
                        icon={<ErrorOutlineIcon color="error" />}
                        iconPosition="start"
                        label={`Errors (${errorSummaryItems.length})`}
                        sx={{
                          textTransform: "none",
                          borderRadius: "15px 15px 0 0",
                          border: "1px solid #a3a3a3",
                          mr: 1,
                          backgroundColor:
                            summaryTab === 0 ? "#f44336" : "#eee",
                          color: summaryTab === 0 ? "#ffffff" : "#000000",
                          "&.Mui-selected": {
                            backgroundColor: "#f44336",
                            color: "#ffffff",
                          },
                          "&:hover": {
                            backgroundColor: "#ff6f6f",
                          },
                        }}
                      />
                    )}

                    {warningSummaryItems.length > 0 && (
                      <Tab
                        icon={<WarningAmberIcon sx={{ color: "#ffd500" }} />}
                        iconPosition="start"
                        label={`Warnings (${warningSummaryItems.length})`}
                        sx={{
                          textTransform: "none",
                          borderRadius: "15px 15px 0 0",
                          border: "1px solid #a3a3a3",
                          backgroundColor:
                            (errorSummaryItems.length === 0 &&
                              summaryTab === 0) ||
                            (errorSummaryItems.length > 0 && summaryTab === 1)
                              ? "#ffd500"
                              : "#eee",
                          color: "#000000",
                          "&.Mui-selected": {
                            backgroundColor: "#ffd500",
                            color: "#000000",
                          },
                          "&:hover": {
                            backgroundColor: "#ffe36a",
                          },
                        }}
                      />
                    )}
                  </Tabs>
                )}

                {(() => {
                  const availableTabs = [];
                  if (errorSummaryItems.length > 0)
                    availableTabs.push("errors");
                  if (warningSummaryItems.length > 0)
                    availableTabs.push("warnings");

                  const active = availableTabs[summaryTab];

                  if (active === "errors") {
                    return (
                      <ErrorSummary
                        errorSteps={errorSummaryItems}
                        steps={steps}
                        onNavigate={(stepIndex) => setActiveStep(stepIndex)}
                      />
                    );
                  }

                  if (active === "warnings") {
                    return (
                      <WarningSummary
                        warningSteps={warningSummaryItems}
                        steps={steps}
                        onNavigate={(stepIndex) => setActiveStep(stepIndex)}
                      />
                    );
                  }

                  return null;
                })()}
              </>
            )}
            {React.cloneElement(steps[activeStep]?.content, {
              formData,
              setFormData,
              stepErrors: errors[activeStep],
              handleChange,
              isReadOnly,
              scholarshipYear,
              setActiveStep,
              appID,
              onBursaryOptOutChange: saveBursaryOptOutImmediate,
            })}
          </Paper>
          <Box className="application-blank-page-buttons-group">
            <Button
              onClick={() => handleSaveAndExit(false)}
              variant="contained"
              color="secondary"
              disabled={isSavingSteps || !canUserEdit}
              startIcon={isSavingSteps && <CircularProgress size={20} />}
            >
              {isSavingSteps ? "Saving..." : "Save and Exit"}
            </Button>
            <Button
              onClick={handlePreviewDialogOpen}
              variant="contained"
              color="secondary"
            >
              Preview / PRINT / Download
            </Button>
            {activeStep < steps?.length - 1 ? (
              <Button
                onClick={() => handleSaveAndContinue(false)}
                variant="contained"
                color="primary"
                disabled={isSavingSteps || !canUserEdit}
                startIcon={isSavingSteps && <CircularProgress size={20} />}
              >
                {isSavingSteps ? "Saving..." : "Save and Continue"}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => setShowConfirmSubmit(true)}
                  variant="contained"
                  color="success"
                  disabled={isSavingSteps || !canUserEdit || hasErrors}
                  startIcon={isSavingSteps && <CircularProgress size={20} />}
                >
                  {user?.application_status === "Submitted"
                    ? "Submitted"
                    : isSavingSteps
                    ? "Submitting..."
                    : "Submit Application"}
                </Button>
                <ConfirmSubmitDialog
                  open={showConfirmSubmit}
                  onClose={() => setShowConfirmSubmit(false)}
                  onConfirm={() => {
                    setShowConfirmSubmit(false);
                    handleSaveAndContinue(true);
                  }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
      <PDFPreviewModal
        open={openPreviewDialog}
        onClose={handlePreviewDialogClose}
        appID={appID}
      />
    </div>
  );
}

export default memo(GeneralApplicationBlankPage);
